import { useCallback } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { authClient } from '@/lib/auth-client';
import type { AuthSession } from '@/lib/auth-client';
import { queryKeys } from '@/lib/query-keys';
import { toAuthError } from '../error';
import { captchaRequestOptions } from '../captcha/captcha-api';

/**
 * The authoritative "who is signed in" read.
 *
 * It goes through the API's native endpoint (`GET /api/auth/get-session`) rather than the
 * client's cached store: the route guards need a promise they can await before rendering,
 * and the API disables the session cookie cache server-side, so this is the same answer
 * every business request would get.
 */
export const sessionQueryOptions = queryOptions({
  queryKey: queryKeys.session,
  queryFn: async (): Promise<AuthSession | null> => {
    const { data, error } = await authClient.getSession();
    if (error) throw toAuthError(error);
    return data ?? null;
  },
});

export const useSession = () => useQuery(sessionQueryOptions);

export const useCurrentUser = () => useSession().data?.user;

/**
 * One field takes either an address or a username: the account an operator was given
 * may be `admin`, an address, or both (`POST /api/auth/sign-in/username` exists because
 * the API installs the username plugin).
 */
export type SignInInput = {
  readonly identifier: string;
  readonly password: string;
  readonly captchaToken?: string;
};
export type SignUpInput = {
  readonly name: string;
  readonly email: string;
  readonly password: string;
  readonly captchaToken?: string;
};

/**
 * What a sign-in answered.
 *
 * `twoFactorRedirect` means the credentials were accepted but **no session was created**:
 * the account has two-step verification on and the API wants a TOTP (or recovery) code
 * first. The login page has to keep the operator where they are and ask for it.
 */
export type SignInResult = {
  readonly twoFactorRedirect: boolean;
};

/**
 * Reads the flag better-auth puts on the sign-in answer.
 *
 * The plugin adds it in an `after` hook, so it is on the wire but not on the endpoint's
 * inferred type in better-auth 1.7.6 — hence a runtime narrowing here instead of a cast at
 * each call site. A response without the flag is an ordinary successful sign-in.
 */
export const isTwoFactorRedirect = (data: unknown): boolean =>
  typeof data === 'object' &&
  data !== null &&
  (data as { twoFactorRedirect?: unknown }).twoFactorRedirect === true;

/**
 * Signing in through the native endpoint sets the HttpOnly session cookie, so the only
 * thing left is to drop the cached "nobody is signed in" answer: the guards read that
 * cache, and without invalidating it a login would land on a page that thinks it is
 * anonymous.
 *
 * When the answer is a two-factor redirect there is nothing to invalidate — no cookie was
 * set — and the caller continues with the second step.
 */
export const useSignIn = () => {
  const client = useQueryClient();
  return useCallback(
    async ({ identifier, password, captchaToken }: SignInInput): Promise<SignInResult> => {
      // The two native endpoints validate their input differently — `/sign-in/email`
      // parses the value as an address, `/sign-in/username` looks up `auth_user.username`
      // — so the form has to pick one. The username plugin only accepts `[A-Za-z0-9_.]`,
      // which is what makes an `@` an unambiguous signal for the address path.
      const { data, error } = identifier.includes('@')
        ? await authClient.signIn.email({ email: identifier, password, ...captchaRequestOptions(captchaToken) })
        : await authClient.signIn.username({ username: identifier, password, ...captchaRequestOptions(captchaToken) });
      if (error) throw toAuthError(error);
      if (isTwoFactorRedirect(data)) return { twoFactorRedirect: true };
      await client.invalidateQueries({ queryKey: queryKeys.session });
      return { twoFactorRedirect: false };
    },
    [client],
  );
};

/**
 * Registration creates two rows on the server — the auth identity and the business user —
 * and signs it in only when email verification is optional. A null token keeps the
 * registration page on the verification instructions instead of navigating as logged in.
 */
export const useSignUp = () => {
  const client = useQueryClient();
  return useCallback(
    async ({ name, email, password, captchaToken }: SignUpInput): Promise<{ requiresEmailVerification: boolean }> => {
      const { data, error } = await authClient.signUp.email({
        name,
        email,
        password,
        ...captchaRequestOptions(captchaToken),
      });
      if (error) throw toAuthError(error);
      await client.invalidateQueries({ queryKey: queryKeys.session });
      return { requiresEmailVerification: data?.token === null };
    },
    [client],
  );
};

/**
 * Signing out clears every cached answer, not just the session.
 *
 * The cache holds other people's data (the user list an admin was reading); leaving it
 * behind would show it to whoever signs in next on this machine.
 */
export const useSignOut = () => {
  const client = useQueryClient();
  return useCallback(async (): Promise<void> => {
    const { error } = await authClient.signOut();
    if (error) throw toAuthError(error);
    client.clear();
  }, [client]);
};
